import {
    ColumnDef, ColumnFiltersState, OnChangeFn, flexRender, getCoreRowModel, getFilteredRowModel, useReactTable
} from "@tanstack/react-table";
import { Box, Spinner, Text, useColorMode } from "theme-ui";
import { useRef, useLayoutEffect, useState } from 'react';
import { TabelClassname } from "../../utils/Classname";
import FilterPopup from "../forms-component/filterPopup";
import CommentPopup from "../pages/demotable/CommentPopup";
import TablePagination from "./Pagination";
import theme from "../../theme";
interface DataTableProps<T extends object> {
    data: T[];
    columns: ColumnDef<T>[];
    className?: string | undefined;
    onPageChange: (page: number | any) => void,
    onPerPageChange: (value: string) => void,
    currentPage?: any,
    TotalCount?: number;
    TotalLength?: any;
    perPageCount: number,
    showNavigation?: boolean;
    enableColumnFilters?: boolean,
    onColumnFiltersChange?: OnChangeFn<ColumnFiltersState> | any,
    loader?: boolean;
    fixedColumn?: boolean;
    withoutURLFilter?: boolean;
}
const DataTable = <T extends object>({
    data,
    columns,
    className = '',
    onPageChange,
    currentPage,
    TotalCount,
    showNavigation = true,
    TotalLength,
    onPerPageChange,
    perPageCount,
    enableColumnFilters = true,
    onColumnFiltersChange,
    loader = false,
    fixedColumn,
    withoutURLFilter
}: DataTableProps<T>) => {

    const commonPadding = localStorage.getItem("tPadding") || '13px';
    // refs to header cells so we can measure widths for sticky offsets
    const headerCellRefs = useRef<Record<string, HTMLTableCellElement | null>>({});
    const [rightOffsets, setRightOffsets] = useState<{ delete: number; createdAt: number }>({ delete: 0, createdAt: 0 });

    useLayoutEffect(() => {
        const measure = () => {
            const deleteEl = headerCellRefs.current['Delete'] || headerCellRefs.current['Action'];
            const deleteWidth = deleteEl?.getBoundingClientRect().width || 0;
            const createdAtOffset = Math.round(deleteWidth);
            setRightOffsets({ delete: 0, createdAt: createdAtOffset });
        };
        measure();
        const onResize = () => measure();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [data, columns]);
    const table = useReactTable({
        data: data,
        columns: columns,
        getCoreRowModel: getCoreRowModel(),
        enableColumnFilters: enableColumnFilters,
        pageCount: TotalLength / perPageCount,
        manualPagination: true,
        enableFilters: true,
        enableGlobalFilter: true,
        getFilteredRowModel: getFilteredRowModel(),
        onColumnFiltersChange: onColumnFiltersChange,
    });
    const [colorMode] = useColorMode();
    const thBackground = colorMode === 'light' ? '#f7efe5' : '#374358';
    const thColor = colorMode === 'light' ? 'black' : '#ffffff';
    const separatorColor = '#D3D5D9';
    const separatorWidth = '1px';

    return (
        <div className="table-responsive">
            <Box as="div" sx={{ paddingLeft: '15px', paddingRight: '15px' }}>
                <div className={`${fixedColumn ? "tableNotFixHead" : "tableFixHead"}`} style={{ borderRadius: 10, border: `1px solid ${theme.colors?.muted}` }}>
                    <table className={`${className + TabelClassname?.PrimaryTabel} mb-0 table-hover`}>
                        <thead className="position-relative">
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header: any, index) => {
                                        const isCommentsHeader = header.column.columnDef.header === "Comments";

                                        let specificClass = '';
                                        const headerName = header.column.columnDef.header;
                                        if (["Created At"].includes(headerName)) {
                                            specificClass = 'th-no-of-tasks';
                                        } else if (["Description", "Name"].includes(headerName)) {
                                            specificClass = 'th-quote-number';
                                        } else if (["Version", "Major/Minor", "Link", "Delete", "Action"].includes(headerName)) {
                                            specificClass = 'th-vc-pc-re';
                                        }

                                        // Sticky right positioning for Created At and Delete/Action columns
                                        const stickyRightStyle: any = {};
                                        if (headerName === 'Delete' || headerName === 'Action') {
                                            stickyRightStyle.position = 'sticky';
                                            stickyRightStyle.right = `${rightOffsets.delete}px`;
                                            stickyRightStyle.zIndex = 5;
                                            stickyRightStyle.background = thBackground;
                                        } else if (headerName === 'Created At') {
                                            // place Created At just to the left of Delete/Action; offset computed from measured delete width
                                            stickyRightStyle.position = 'sticky';
                                            stickyRightStyle.right = `${rightOffsets.createdAt}px`;
                                            stickyRightStyle.zIndex = 5;
                                            stickyRightStyle.background = thBackground;
                                        }
                                        // Add vertical border for Created At and Action/Delete columns
                                        if (headerName === 'Description') {
                                            stickyRightStyle.borderRight = `${separatorWidth} solid ${separatorColor}`;
                                        }
                                        if (headerName === 'Created At' || headerName === 'Delete' || headerName === 'Action') {
                                            stickyRightStyle.boxShadow = `inset 1px 0 0 0 ${separatorColor}`;
                                        }

                                        return (
                                            <th
                                                ref={(el) => { headerCellRefs.current[headerName] = el; }}
                                                className={`th align-middle border-top-0 border-bottom-0 fixed-column-${index} ${specificClass}`}
                                                key={index}
                                                style={{ backgroundColor: thBackground, padding: `${commonPadding}px`, fontSize: 14, color: thColor, ...stickyRightStyle }}>
                                                {header.column.getCanFilter() &&
                                                    <FilterPopup column={header.column.columnDef} id={`column${index}`} withoutURLFilter={withoutURLFilter} />}
                                                <Text variant='Poppins16Normal25lh' as={"span"} className="text-center position-relative">
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                    {isCommentsHeader && <CommentPopup />}
                                                </Text>
                                            </th>
                                        );
                                    })}
                                </tr>
                            ))}

                        </thead>
                        <tbody>
                            {!loader && (
                                table.getRowModel().rows.map((row, rowIndex) => (
                                    <tr key={rowIndex} data-state={row.getIsSelected() && "selected"}
                                        style={{
                                            overflow: 'hidden',

                                            borderBottom:
                                                (rowIndex + 1) === table.getRowModel().rows?.length ? 'none' :
                                                    '1px solid #D3D5D9',
                                        }}
                                    >
                                        {row.getVisibleCells().map((cell, cellIndex) => {
                                            const cellHeader = cell.column.columnDef.header;
                                            const cellStickyStyle: any = {};
                                            if (cellHeader === 'Delete' || cellHeader === 'Action') {
                                                cellStickyStyle.position = 'sticky';
                                                cellStickyStyle.right = `${rightOffsets.delete}px`;
                                                cellStickyStyle.zIndex = 4;
                                                cellStickyStyle.background = colorMode === 'light' ? '#fdfdfd' : '#282e42';
                                            } else if (cellHeader === 'Created At') {
                                                cellStickyStyle.position = 'sticky';
                                                cellStickyStyle.right = `${rightOffsets.createdAt}px`;
                                                cellStickyStyle.zIndex = 4;
                                                cellStickyStyle.background = colorMode === 'light' ? '#fdfdfd' : '#282e42';
                                            }
                                            // Add vertical border before Created At and Action/Delete cells
                                            if (cellHeader === 'Description') {
                                                cellStickyStyle.borderRight = `${separatorWidth} solid ${separatorColor}`;
                                            }
                                            if (cellHeader === 'Created At' || cellHeader === 'Delete' || cellHeader === 'Action') {
                                                cellStickyStyle.boxShadow = `inset 1px 0 0 0 ${separatorColor}`;
                                            }

                                            return (
                                                <td className={"fixed-column-" + cellIndex + " table-cell"} key={cell.id} style={{
                                                    padding: `${commonPadding}px`,
                                                    background: colorMode === 'light' ? '#fdfdfd' : '#282e42',
                                                    ...cellStickyStyle
                                                }}>
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            )
                                        })}
                                    </tr>
                                ))
                            )}

                        </tbody>

                    </table>
                    {loader &&
                        <Spinner sx={{ position: "absolute", top: "50%", left: "50%" }} />
                    }
                    {!loader && data?.length === 0 && (
                        <Text sx={{ position: "absolute", top: "50%", left: "50%", color: thColor }}>No Data Found!</Text>
                    )}
                </div>
            </Box>
            {showNavigation ?
                <Box className="sticky-navigation">
                    <TablePagination
                        // className="sticky-navigation"
                        onPageChange={(page) => { onPageChange(page) }}
                        onPerPageChange={onPerPageChange}
                        TotalCount={TotalCount}
                        perPageCount={perPageCount}
                        TotalLength={TotalLength}
                        CurrentPage={currentPage}

                    /></Box> : null
            }
        </div>
    );
};

export default DataTable;
